import { ReactNode, useEffect } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { getRouteCompanyId } from "features/auth/access-lost";
import { fetchCabinetSummaryThunk } from "./cabinet-summary.slice";

/** The same top of every queue page: a way back to «Užduotys» and the queue's name. */
export default function QueueFrame({
  companyId,
  title,
  children,
}: {
  companyId: string;
  title: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  useRefreshSummaryOnLeave(companyId);

  return (
    <div className="w-full px-4 py-6 lg:px-8">
      <Link
        to={`/app/${companyId}/my-work`}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 hover:underline max-md:min-h-11"
      >
        ‹ {t("tasks")}
      </Link>
      <h1 className="mb-4 mt-2 text-2xl font-semibold text-gray-900">{title}</h1>
      {children}
    </div>
  );
}

/**
 * Asks for the summary again when the page goes away or its company changes. Whatever was
 * signed, reviewed, approved or passed on the page moves the bell and the menu at that
 * moment, not at the next two-minute refresh. But when the address already belongs to
 * another company (the person switched company), that company's own load asks for its
 * summary; asking for this one would turn the shared summary back to the company just left.
 */
function useRefreshSummaryOnLeave(companyId: string | undefined) {
  const dispatch = useAppDispatch();
  useEffect(
    () => () => {
      if (companyId && getRouteCompanyId(window.location.pathname) === companyId) {
        dispatch(fetchCabinetSummaryThunk(companyId));
      }
    },
    [dispatch, companyId]
  );
}

/** Wraps the training pages, which have no queue frame: a finished test leaves the task list. */
export function TrainingPagesFrame() {
  const { companyId } = useParams();
  useRefreshSummaryOnLeave(companyId);
  return <Outlet />;
}
